"use client";

import Link from "next/link";
import { centsToDollars, getCartCheckoutState, getFreeShippingProgress } from "@/lib/cart/view-model";
import { formatPrice } from "@/lib/utils";
import type { ShopifyMoneyV2 } from "@/types/shopify";

interface CartSummaryProps {
  subtotal: ShopifyMoneyV2 | null | undefined;
  checkoutUrl: string | null | undefined;
  isLoading: boolean;
  showViewCartLink?: boolean;
  onNavigate?: () => void;
}

export function CartSummary({
  subtotal,
  checkoutUrl,
  isLoading,
  showViewCartLink,
  onNavigate,
}: CartSummaryProps) {
  const checkout = getCartCheckoutState({ isLoading, checkoutUrl });
  // Null while no approved free-shipping threshold exists, so the cart shows
  // no shipping promise rather than an invented one.
  const progress = subtotal ? getFreeShippingProgress(subtotal.amount) : null;

  return (
    <div className="space-y-3">
      {progress && !progress.qualifies && (
        <div className="text-xs" style={{ color: "#9A9A9A" }}>
          Add{" "}
          <span className="font-semibold text-[#F5F5F5]">
            {formatPrice(centsToDollars(progress.remainingCents))}
          </span>{" "}
          more to reach free shipping
          <div
            className="mt-1.5 h-1 rounded-full overflow-hidden"
            style={{ backgroundColor: "#2A2A2A" }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                backgroundColor: "#E8C547",
                width: `${progress.percent}%`,
              }}
            />
          </div>
        </div>
      )}

      {progress?.qualifies && (
        <p className="text-xs font-semibold" style={{ color: "#40C070" }}>
          Free shipping threshold reached.
        </p>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm" style={{ color: "#9A9A9A" }}>Subtotal</span>
        <span className="text-base font-semibold">
          {subtotal ? formatPrice(subtotal.amount) : "--"}
        </span>
      </div>
      <p className="text-xs" style={{ color: "#8A8A8A" }}>
        Final taxes, discounts, and shipping are calculated at checkout.
      </p>

      {checkout.disabled ? (
        <button
          type="button"
          disabled
          className="flex items-center justify-center w-full py-3 text-sm font-bold rounded-sm opacity-50"
          style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
        >
          {checkout.label}
        </button>
      ) : (
        <a
          href={checkoutUrl ?? "#"}
          onClick={onNavigate}
          className="flex items-center justify-center w-full py-3 text-sm font-bold rounded-sm transition-all hover:brightness-110"
          style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
        >
          {checkout.label}
        </a>
      )}

      {showViewCartLink && (
        <Link
          href="/cart"
          onClick={onNavigate}
          className="flex items-center justify-center w-full py-2.5 text-sm font-semibold rounded-sm border transition-colors hover:text-[#F5F5F5]"
          style={{ borderColor: "#2A2A2A", color: "#E8C547" }}
        >
          View Cart
        </Link>
      )}
    </div>
  );
}
